import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { type Channel, type Page, PLATFORMS, type Platform } from '@regia/shared';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { errorMessage } from '../../lib/api';
import { PLATFORM_LABELS, PlatformIcon } from '../../lib/platforms';
import {
  Badge,
  Button,
  ConfirmDialog,
  Field,
  Input,
  ListGroup,
  ListRow,
  Section,
  Select,
  Sheet,
} from '../../ui';
import { useAddChannel, useRemoveChannel, useUpdateChannel } from './api';
import { ChannelSource } from './ChannelSource';

const HANDLE_HINT: Record<Platform, string> = {
  facebook: 'Il nome della pagina o l’indirizzo facebook.com/…',
  instagram: 'Il nome utente, per esempio @neferbenessere',
  google_business: 'Il nome della scheda su Google Maps',
  website: 'L’indirizzo del sito, per esempio https://neferbenessere.it',
  tiktok: 'Il nome utente, per esempio @nome',
  youtube: 'Il nome del canale',
  linkedin: 'Il nome della pagina',
};

interface Draft {
  platform: Platform;
  label: string;
  handle: string;
  url: string;
  externalId: string;
  postsPerWeek: string;
  config: Record<string, string>;
}

const emptyDraft: Draft = {
  platform: 'instagram',
  label: '',
  handle: '',
  url: '',
  externalId: '',
  postsPerWeek: '',
  config: {},
};

function toDraft(c: Channel): Draft {
  return {
    platform: c.platform,
    label: c.label,
    handle: c.handle,
    url: c.url,
    externalId: c.externalId,
    postsPerWeek: c.postsPerWeek === null ? '' : String(c.postsPerWeek),
    config: c.config ?? {},
  };
}

export function ChannelsEditor({ page }: { page: Page }) {
  const add = useAddChannel(page.id);
  const update = useUpdateChannel();
  const remove = useRemoveChannel();
  const [editing, setEditing] = useState<Channel | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (target: Channel | 'new') => {
    setEditing(target);
    setDraft(target === 'new' ? emptyDraft : toDraft(target));
    setError(null);
  };
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const posts = draft.postsPerWeek.trim() === '' ? null : Number(draft.postsPerWeek);
    if (posts !== null && (!Number.isInteger(posts) || posts < 0 || posts > 50)) {
      setError('Scrivi un numero intero tra 0 e 50');
      return;
    }
    const common = {
      label: draft.label,
      handle: draft.handle,
      url: draft.url,
      externalId: draft.externalId,
      postsPerWeek: posts,
      config: draft.config,
    };
    try {
      if (editing === 'new') await add.mutateAsync({ platform: draft.platform, ...common });
      else if (editing) await update.mutateAsync({ id: editing.id, input: common });
      toast.success(editing === 'new' ? 'Canale aggiunto' : 'Canale salvato');
      setEditing(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function doDelete() {
    if (!editing || editing === 'new') return;
    try {
      await remove.mutateAsync(editing.id);
      toast.success('Canale eliminato');
      setConfirmDelete(false);
      setEditing(null);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <Section
      title="Canali"
      action={
        <Button variant="ghost" size="sm" icon={PlusIcon} onClick={() => open('new')}>
          Aggiungi
        </Button>
      }
    >
      {page.channels.length === 0 ? (
        <button
          type="button"
          onClick={() => open('new')}
          className="flex w-full items-center gap-3 rounded-[var(--radius-sheet)] border border-dashed border-line px-4 py-5 text-left text-[0.9375rem] text-ink-2 hover:bg-sheet"
        >
          <PlusIcon size={20} aria-hidden />
          Aggiungi Facebook, Instagram, la scheda Google o il sito
        </button>
      ) : (
        <ListGroup>
          {page.channels.map((c) => (
            <ListRow
              key={c.id}
              onClick={() => open(c)}
              chevron
              leading={<PlatformIcon platform={c.platform} size={22} className="text-ink" />}
              title={c.label || c.handle || PLATFORM_LABELS[c.platform]}
              detail={[
                PLATFORM_LABELS[c.platform],
                c.postsPerWeek ? `${c.postsPerWeek} post a settimana` : null,
              ]
                .filter(Boolean)
                .join(', ')}
              trailing={c.status === 'errore' ? <Badge tone="alert">Errore</Badge> : null}
            />
          ))}
        </ListGroup>
      )}

      <Sheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing === 'new' ? 'Nuovo canale' : PLATFORM_LABELS[draft.platform]}
        size="sm"
        footer={
          <>
            {editing !== 'new' ? (
              <Button
                variant="ghost"
                icon={TrashIcon}
                className="text-alert md:mr-auto"
                onClick={() => setConfirmDelete(true)}
              >
                Elimina
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Annulla
            </Button>
            <Button type="submit" form="channel-form" loading={add.isPending || update.isPending}>
              Salva
            </Button>
          </>
        }
      >
        <form id="channel-form" onSubmit={save} className="flex flex-col gap-4 pt-2" noValidate>
          {editing === 'new' ? (
            <Field label="Dove">
              {(id) => (
                <Select
                  id={id}
                  value={draft.platform}
                  onChange={(e) => set('platform', e.target.value as Platform)}
                >
                  {PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {PLATFORM_LABELS[p]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          <ChannelSource draft={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
          <Field label="Nome o indirizzo" hint={HANDLE_HINT[draft.platform]}>
            {(id, describedBy) => (
              <Input
                id={id}
                value={draft.handle}
                onChange={(e) => set('handle', e.target.value)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
          {draft.platform !== 'website' ? (
            <Field label="Link" optional>
              {(id) => (
                <Input
                  id={id}
                  type="url"
                  inputMode="url"
                  value={draft.url}
                  onChange={(e) => set('url', e.target.value)}
                  placeholder="https://"
                />
              )}
            </Field>
          ) : null}
          {draft.platform !== 'website' ? (
            <Field
              label="Post a settimana"
              optional
              hint="Regia ti avvisa se resti indietro"
              error={error ?? undefined}
            >
              {(id, describedBy) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  value={draft.postsPerWeek}
                  onChange={(e) =>
                    set('postsPerWeek', e.target.value.replace(/\D/g, '').slice(0, 2))
                  }
                  placeholder="3"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          ) : null}
          <Field label="Etichetta" optional hint="Per distinguere due canali dello stesso tipo">
            {(id, describedBy) => (
              <Input
                id={id}
                value={draft.label}
                onChange={(e) => set('label', e.target.value)}
                aria-describedby={describedBy}
              />
            )}
          </Field>
          {draft.platform === 'website' && error ? (
            <p className="text-sm text-alert">{error}</p>
          ) : null}
        </form>
      </Sheet>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminare il canale?"
        message="Si perdono anche i numeri raccolti per questo canale."
        confirmLabel="Elimina canale"
        danger
        loading={remove.isPending}
        onConfirm={doDelete}
      />
    </Section>
  );
}
